// Team accent persistence helper.
//
// Mirrors themePersist.ts exactly (see the rationale there): the server sync
// via /api/settings/all can fail silently offline, so the accent choice is
// mirrored to Capacitor Preferences (native) + localStorage (web/dev) and
// survives a relaunch regardless of network state.
//
// "nexus" is the default brand green. The faction accents are a Pro perk —
// persistence stores the CHOICE; whether it is APPLIED is gated on the live
// entitlement in SessionProvider, so a lapsed subscription falls back to
// green without losing the trainer's pick.

export type ThemeMode = "auto" | "light" | "dark";
export type AccentTheme = "nexus" | "instinct" | "valor" | "mystic";

const KEY = "nexus-dex.accentTheme";
const VALID = new Set<string>(["nexus", "instinct", "valor", "mystic"]);

function readLocalStorage(): AccentTheme | null {
  try {
    if (typeof window === "undefined") return null;
    const v = window.localStorage.getItem(KEY);
    return v && VALID.has(v) ? (v as AccentTheme) : null;
  } catch {
    return null;
  }
}

function writeLocalStorage(value: AccentTheme): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(KEY, value);
  } catch {
    /* sandboxed iframes may block storage; ignore. */
  }
}

async function readCapacitorPrefs(): Promise<AccentTheme | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    const res = await Preferences.get({ key: KEY });
    const v = res?.value;
    return v && VALID.has(v) ? (v as AccentTheme) : null;
  } catch {
    return null;
  }
}

async function writeCapacitorPrefs(value: AccentTheme): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    await Preferences.set({ key: KEY, value });
  } catch {
    /* not on a Capacitor runtime; localStorage is the source of truth. */
  }
}

export function readPersistedAccentThemeSync(): AccentTheme | null {
  return readLocalStorage();
}

export async function readPersistedAccentTheme(): Promise<AccentTheme | null> {
  const local = readLocalStorage();
  if (local) return local;
  return await readCapacitorPrefs();
}

export function persistAccentTheme(value: AccentTheme): void {
  if (!VALID.has(value)) return;
  writeLocalStorage(value);
  void writeCapacitorPrefs(value);
}
